<template>
  <div class="page-flights">
    <base-timeline></base-timeline>

    <base-section class="no-padding-bottom" v-if="departureFlights.length">
      <div class="container">
        <h2 class="title title-bigger title-semi text-center">{{$t('booking.departure_flights')}}</h2>

        <div class="flights-header">
          <div class="flights-header-content">
            <span class="text text-huge text-caps text-clear">{{ origin }}</span>
            <i class="fa-light fa-plane"></i>
            <span class="text text-huge text-caps text-clear">{{ destination }}</span>
          </div>
        </div>

        <div class="flights">
          <base-card
            class="base-card-flight flight"
            :class="{
              active: selectedDepartureFlight && selectedDepartureFlight.slot_id === flight.slot_id,
              unavailable: !isFlightAvailable( flight ),
              'tooltip-open': isTooltipOpen( flight, 'departure' )
            }"
            v-for="flight in departureFlights" :key="flight.slot_id"
            @click="selectDepartureFlight( flight, $event )"
          >
            <div class="flight-date">
              <span class="day-name text text-big text-caps text-clear">{{ convertDepartureDate.dayName }}</span>
              <span class="day-number">{{ convertDepartureDate.dayNumber }}</span>
              <span class="month-name text text-big text-caps text-clear">{{ convertDepartureDate.monthName }}</span>
            </div>

            <div class="flight-time">
              <div class="duration">
                <hr>
                <div class="duration-time">
                  <i class="fa-regular fa-clock"></i>
                  <span class="time text text-big text-semi text-clear">{{ getDuration( flight.departure_time, flight.arrival_time ) }}</span>
                </div>
              </div>

              <div class="schedule">
                <span class="departure text text-big text-semi text-clear">{{ formatTime( flight.departure_time ) }}</span>
                <span class="arrival text text-big text-semi text-clear">{{ formatTime( flight.arrival_time ) }}</span>
              </div>
            </div>

            <div class="flight-seats">
              <span class="seats-number">{{ flight.available_seats }}</span>
              <span class="seats-text text text-smallest text-caps text-clear">{{$t('booking.seats_available')}}</span>
            </div>

            <span
              v-if="!isFlightAvailable( flight )"
              class="flight-tooltip"
            >
              {{ $t('booking.not_enough_seats') }}
            </span>
          </base-card>
        </div>
      </div>
    </base-section>

    <base-section class="no-padding-bottom" v-if="returnFlights.length">
      <div class="container">
        <h2 class="title title-bigger title-semi text-center">{{$t('booking.return_flights')}}</h2>

        <div class="flights-header">
          <div class="flights-header-content">
            <span class="text text-huge text-caps text-clear">{{ destination }}</span>
            <i class="fa-light fa-plane"></i>
            <span class="text text-huge text-caps text-clear">{{ origin }}</span>
          </div>
        </div>

        <div class="flights">
          <base-card
            class="base-card-flight flight"
            :class="{
              active: selectedReturnFlight && selectedReturnFlight.slot_id === flight.slot_id,
              unavailable: !isFlightAvailable( flight ),
              'tooltip-open': isTooltipOpen( flight, 'return' )
            }"
            v-for="flight in returnFlights" :key="flight.slot_id"
            @click="selectReturnFlight( flight, $event )"
          >
            <div class="flight-date">
              <span class="day-name text text-big text-caps text-clear">{{ convertReturnDate.dayName }}</span>
              <span class="day-number">{{ convertReturnDate.dayNumber }}</span>
              <span class="month-name text text-big text-caps text-clear">{{ convertReturnDate.monthName }}</span>
            </div>

            <div class="flight-time">
              <div class="duration">
                <hr>
                <div class="duration-time">
                  <i class="fa-regular fa-clock"></i>
                  <span class="time text text-big text-semi text-clear">{{ getDuration( flight.departure_time, flight.arrival_time ) }}</span>
                </div>
              </div>

              <div class="schedule">
                <span class="departure text text-big text-semi text-clear">{{ formatTime( flight.departure_time ) }}</span>
                <span class="arrival text text-big text-semi text-clear">{{ formatTime( flight.arrival_time ) }}</span>
              </div>
            </div>

            <div class="flight-seats">
              <span class="seats-number">{{ flight.available_seats }}</span>
              <span class="seats-text text text-smallest text-caps text-clear">{{$t('booking.seats_available')}}</span>
            </div>

            <span
              v-if="!isFlightAvailable( flight )"
              class="flight-tooltip"
            >
              {{ $t('booking.not_enough_seats') }}
            </span>
          </base-card>
        </div>
      </div>
    </base-section>

    <base-section>
      <div class="container">
        <div class="footer-buttons">
          <base-button class="base-button-outline" @click="goPrevious">{{$t('buttons.prev')}}</base-button>
          <base-button class="base-button-pri" v-if="toggleNextStep" @click="goNext">{{$t('buttons.next')}}</base-button>
        </div>
      </div>
    </base-section>
  </div>

  <teleport to=".simply-flights-frontend">
    <base-loading v-if="loading"></base-loading>
  </teleport>
</template>

<script>
import { mapState } from 'vuex'
import BaseTimeline from '@/components/partials/BaseTimeline.vue'

export default {
  components: {
    BaseTimeline: BaseTimeline,
  },
  data() {
    return {
      selectedDepartureFlight: null,
      selectedReturnFlight: null,
      openTooltipId: null,
    }
  },
  computed: {
    ...mapState( 'optionsModule', [
      'origin',
      'destination',
      'departureDate',
      'returnDate',
      'passengersCount',
      'tripType'
    ]),
    ...mapState( 'flightsModule', [
      'departureFlights',
      'returnFlights',
      'loading'
    ]),
    convertDepartureDate() {
      if (!this.departureDate) {
        return { dayName: '', dayNumber: '', monthName: '' }
      }

      const [y, m, d] = this.departureDate.split('-').map(Number)
      const date = new Date(y, m - 1, d)

      const locale = this.$i18n.locale === 'fr' ? 'fr-CA' : 'en-CA'

      return {
        dayName: date.toLocaleDateString(locale, { weekday: 'long' }),
        dayNumber: d, // 👈 explicit, zero ambiguity
        monthName: date.toLocaleDateString(locale, { month: 'long' })
      }
    },
    convertReturnDate() {
      if (!this.returnDate) {
        return { dayName: '', dayNumber: '', monthName: '' }
      }

      const [y, m, d] = this.returnDate.split('-').map(Number)
      const date = new Date(y, m - 1, d)

      const locale = this.$i18n.locale === 'fr' ? 'fr-CA' : 'en-CA'

      return {
        dayName: date.toLocaleDateString(locale, { weekday: 'long' }),
        dayNumber: d, // 👈 explicit, zero ambiguity
        monthName: date.toLocaleDateString(locale, { month: 'long' })
      }
    },
    toggleNextStep() {
      if( !this.selectedDepartureFlight || !this.isFlightAvailable( this.selectedDepartureFlight ) ) {
        return false
      }

      if( this.returnFlights.length ) {
        return this.selectedReturnFlight !== null && this.isFlightAvailable( this.selectedReturnFlight )
      }

      return true
    }
  },
  methods: {
    formatTime( time ) {
      if( !time ) return ''
      const [hours, minutes] = time.split( ':' ).map( Number )
      const date = new Date()
      date.setHours( hours, minutes, 0 )
      const locale = this.$i18n.locale === 'en' ? 'en-US' : 'fr-CA'

      return new Intl.DateTimeFormat( locale, {
        hour: 'numeric',
        minute: '2-digit',
        hour12: this.$i18n.locale === 'en'
      }).format( date )
    },
    getDuration( start, end ) {
      if (!start || !end) return ''

      const [sh, sm] = start.split(':').map(Number)
      const [eh, em] = end.split(':').map(Number)

      const startMinutes = sh * 60 + sm
      const endMinutes = eh * 60 + em

      let diff = endMinutes - startMinutes

      // Safety for overnight flights (optional)
      if (diff < 0) {
        diff += 24 * 60
      }

      const hours = Math.floor(diff / 60)
      const minutes = diff % 60

      if (minutes === 0) {
        return `${hours}h`
      }

      return `${hours}h ${minutes}m`
    },
    isFlightAvailable( flight ) {
      const seats = Math.max( 0, Number( flight.available_seats ) || 0 )
      return seats >= this.passengersCount
    },
    tooltipId( flight, leg ) {
      return `${leg}-${flight.slot_id}`
    },
    isTooltipOpen( flight, leg ) {
      return this.openTooltipId === this.tooltipId( flight, leg )
    },
    hideUnavailableTooltip() {
      this.openTooltipId = null
    },
    showUnavailableTooltip( flight, leg, event ) {
      event.stopPropagation()
      this.openTooltipId = this.tooltipId( flight, leg )
    },
    selectDepartureFlight( flight, event ) {
      if( !this.isFlightAvailable( flight ) ) {
        this.showUnavailableTooltip( flight, 'departure', event )
        return
      }

      this.hideUnavailableTooltip()
      this.selectedDepartureFlight = flight
    },
    selectReturnFlight( flight, event ) {
      if( !this.isFlightAvailable( flight ) ) {
        this.showUnavailableTooltip( flight, 'return', event )
        return
      }

      this.hideUnavailableTooltip()
      this.selectedReturnFlight = flight
    },
    goPrevious() {
      this.$store.commit( 'flightsModule/CLEAR_FLIGHTS' )
      this.$router.push( '/reservation-options' )
    },
    goNext() {
      if( !this.toggleNextStep ) {
        return
      }

      this.$store.commit( 'flightsModule/SET_ACTIVE_DEPARTURE_FLIGHT', this.selectedDepartureFlight )
      if( this.selectedReturnFlight !== null ) {
        this.$store.commit( 'flightsModule/SET_ACTIVE_RETURN_FLIGHT', this.selectedReturnFlight )
      }
      this.$router.push( '/reservation-passengers#base-timeline' )
    }
  },
  mounted() {
    this.$store.commit( 'timelineModule/SET_STEP', 1 )
    this.selectedDepartureFlight = this.$store.state.flightsModule.activeDepartureFlight || null
    this.selectedReturnFlight = this.$store.state.flightsModule.activeReturnFlight || null

    document.addEventListener( 'click', this.hideUnavailableTooltip )
    window.addEventListener( 'scroll', this.hideUnavailableTooltip, true )

    if( !this.origin || !this.destination || !this.departureDate || !this.passengersCount ) {
      this.$router.replace('/reservation-options')
      return
    }

    if( !this.departureFlights.length ) {
      this.$store.dispatch('flightsModule/readAvailableFlights', {
        origin: this.origin,
        destination: this.destination,
        departureDate: this.departureDate,
        returnDate: this.tripType === 'round-trip'
          ? this.returnDate
          : null,
        passengersCount: this.passengersCount
      })
    }
  },
  unmounted() {
    document.removeEventListener( 'click', this.hideUnavailableTooltip )
    window.removeEventListener( 'scroll', this.hideUnavailableTooltip, true )
  }
}
</script>